import React, { createContext, useEffect, ReactNode } from 'react';
import { analyticsService } from '@/services/analytics';
import { useAuth } from '@/contexts/AuthContext';
import type { AnalyticsEventData } from '@/services/api';

export interface AnalyticsContextType {
  trackPageView: (path: string, title?: string, additionalData?: Record<string, any>) => void;
  trackUserAction: (action: string, additionalData?: Record<string, any>) => void;
  trackButtonClick: (buttonId?: string, buttonText?: string, additionalData?: Record<string, any>) => void;
  trackFormSubmission: (formId?: string, formType?: string, additionalData?: Record<string, any>) => void;
  trackError: (errorType: string, errorMessage: string, additionalData?: Record<string, any>) => void;
  trackAuthentication: (authAction: 'login' | 'logout' | 'signup' | 'password_reset', authMethod?: 'email' | 'google', additionalData?: Record<string, any>) => void;
  trackCustomEvent: (eventData: AnalyticsEventData) => void;
  getQueueSize: () => number;
  isQueueEmpty: () => boolean;
}

// Create the context
export const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const { user, isAuthenticated } = useAuth();

  // Add user context to all analytics events
  const addUserContext = (additionalData?: Record<string, any>) => {
    const baseContext = {
      user_authenticated: isAuthenticated,
      timestamp: new Date().toISOString(),
      ...additionalData,
    };

    // Always include user_id - use authenticated user ID or generate anonymous ID
    let userId: string;
    if (isAuthenticated && user?.id) {
      userId = user.id;
    } else {
      // Generate or retrieve anonymous user ID for unauthenticated users
      userId = getOrCreateAnonymousUserId();
    }

    return {
      ...baseContext,
      user_id: userId,
    };
  };

  // Helper function to manage anonymous user IDs
  const getOrCreateAnonymousUserId = (): string => {
    const storageKey = 'anonymous_user_id';
    let anonymousId = localStorage.getItem(storageKey);

    if (!anonymousId) {
      // Generate a new anonymous ID
      anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      try {
        localStorage.setItem(storageKey, anonymousId);
      } catch (error) {
        // If localStorage fails, use session-based ID
        console.warn('Failed to store anonymous user ID in localStorage:', error);
      }
    }

    return anonymousId;
  };

  // Track authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      analyticsService.trackUserAction('user_authenticated', {
        user_id: user.id,
        email_verified: user.email_verified,
      });
    }
  }, [isAuthenticated, user]);

  // Error boundary for analytics
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Only track errors if we have a valid user context
      const userContext = addUserContext({
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });

      if (userContext.user_id) {
        analyticsService.trackError('javascript_error', event.message, userContext);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Only track errors if we have a valid user context
      const userContext = addUserContext({
        reason: event.reason,
      });

      if (userContext.user_id) {
        analyticsService.trackError('unhandled_promise_rejection', String(event.reason), userContext);
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Context value
  const contextValue: AnalyticsContextType = {
    trackPageView: (path: string, title?: string, additionalData?: Record<string, any>) => {
      analyticsService.trackPageView(path, title, addUserContext(additionalData));
    },

    trackUserAction: (action: string, additionalData?: Record<string, any>) => {
      analyticsService.trackUserAction(action, addUserContext(additionalData));
    },

    trackButtonClick: (buttonId?: string, buttonText?: string, additionalData?: Record<string, any>) => {
      analyticsService.trackButtonClick(buttonId, buttonText, addUserContext(additionalData));
    },

    trackFormSubmission: (formId?: string, formType?: string, additionalData?: Record<string, any>) => {
      analyticsService.trackFormSubmission(formId, formType, addUserContext(additionalData));
    },

    trackError: (errorType: string, errorMessage: string, additionalData?: Record<string, any>) => {
      analyticsService.trackError(errorType, errorMessage, addUserContext(additionalData));
    },

    trackAuthentication: (authAction: 'login' | 'logout' | 'signup' | 'password_reset', authMethod?: 'email' | 'google', additionalData?: Record<string, any>) => {
      analyticsService.trackAuthentication(authAction, authMethod, addUserContext(additionalData));
    },

    trackCustomEvent: (eventData: AnalyticsEventData) => {
      const enrichedEventData = {
        ...eventData,
        event_data: addUserContext(eventData.event_data),
      };
      analyticsService.trackCustomEvent(enrichedEventData);
    },

    getQueueSize: () => analyticsService.getQueueSize(),
    isQueueEmpty: () => analyticsService.isQueueEmpty(),
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}